/* Cart progress bar — tiered milestones.
   See snippets/pw-free-ship-progress.liquid for stage definitions.
   Mirrors that Liquid logic for client-side updates on cart changes.
   All user-facing strings come from data-tpl-* attributes (locale-driven). */

class PwFreeShipProgress extends HTMLElement {
  connectedCallback() {
    if (!window.subscribe || !window.PUB_SUB_EVENTS) return;
    this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => this.update(e.cartData));
    this.liveRegion = document.getElementById('CartDrawer-LiveRegionText');
  }

  disconnectedCallback() { this.unsub && this.unsub(); }

  formatMoney(cents, currency) {
    // Drops trailing .00 like money_without_trailing_zeros.
    const dollars = cents / 100;
    const hasFraction = Math.round(cents) % 100 !== 0;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    });
  }

  hasCodeDiscount(cart) {
    const apps = cart.cart_level_discount_applications || [];
    return apps.some((a) => a && (a.type === 'discount_code' || a.key));
  }

  bottleLabel(qty) {
    const tpl = qty === 1
      ? (this.dataset.tplBottleOne || '[count] bottle')
      : (this.dataset.tplBottleOther || '[count] bottles');
    return tpl.replace('[count]', qty);
  }

  fill(tplKey, replacement) {
    const tpl = this.dataset[tplKey] || '';
    return tpl.replace('[remaining]', replacement);
  }

  update(cart) {
    if (!cart) return;

    const threshold = parseInt(this.dataset.thresholdCents, 10);
    const tier5 = parseInt(this.dataset.tier5Qty, 10) || 6;
    const tier10 = parseInt(this.dataset.tier10Qty, 10) || 12;
    const total = cart.total_price || 0;
    const qty = cart.item_count || 0;
    const fill = this.querySelector('.pw-ship-progress__fill');
    const msgEl = this.querySelector('.pw-ship-progress__msg');

    if (qty === 0 && total === 0) {
      this.setAttribute('hidden', '');
      return;
    }
    this.removeAttribute('hidden');

    const shipUnlocked = total >= threshold;
    const tier5Unlocked = qty >= tier5;
    const tier10Unlocked = qty >= tier10;
    const hasCode = this.hasCodeDiscount(cart);

    let stage = '';
    let msgHtml = '';
    let pct = 0;
    let liveText = '';

    if (!shipUnlocked) {
      stage = 'ship-progress';
      const remaining = threshold - total;
      const remFmt = this.formatMoney(remaining, cart.currency);
      const remHtml = `<span class="pw-ship-progress__remaining">${remFmt}</span>`;
      msgHtml = this.fill('tplShipProgress', remHtml);
      liveText = this.fill('tplLiveShipProgress', remFmt);
      pct = Math.min(100, (total / threshold) * 100);
    } else if (hasCode) {
      stage = 'ship-unlocked-code';
      msgHtml = this.dataset.tplShipUnlocked || '';
      liveText = msgHtml;
      pct = 100;
    } else if (!tier5Unlocked) {
      stage = 'tier-5-progress';
      const remaining = tier5 - qty;
      const label = this.bottleLabel(remaining);
      const remHtml = `<span class="pw-ship-progress__remaining">${label}</span>`;
      msgHtml = this.fill('tplTier5Progress', remHtml);
      liveText = this.fill('tplLiveTier5Progress', label);
      pct = Math.min(100, (qty / tier5) * 100);
    } else if (!tier10Unlocked) {
      stage = 'tier-10-progress';
      const remaining = tier10 - qty;
      const label = this.bottleLabel(remaining);
      const remHtml = `<span class="pw-ship-progress__remaining">${label}</span>`;
      msgHtml = this.fill('tplTier10Progress', remHtml);
      liveText = this.fill('tplLiveTier10Progress', label);
      const denom = tier10 - tier5;
      const numer = qty - tier5;
      pct = Math.min(100, (numer / denom) * 100);
    } else {
      stage = 'tier-10-unlocked';
      msgHtml = this.dataset.tplTier10Unlocked || '';
      liveText = msgHtml;
      pct = 100;
    }

    this.dataset.stage = stage;
    if (msgEl) msgEl.innerHTML = msgHtml;
    if (fill) fill.style.width = pct + '%';
    if (this.liveRegion) this.liveRegion.textContent = liveText;
  }
}
customElements.define('pw-free-ship-progress', PwFreeShipProgress);
